/**
 * 閲覧イベントの収集（EP-6）。**バスと分析の唯一の接点**。
 *
 * 機能側（engine / overlay / search …）はアダプタを一切知らない。ここがバスを購読して
 * 分析語彙へ翻訳し、バッファへ積み、まとめてアダプタへ配る（coding.md の EP-6 ガードレール
 * 「all analytics flow through the bus; adapters never called directly from features」）。
 *
 * 2 相構成: コンストラクタで購読と資源登録だけ済ませ、宛先（apiBase・slug）が確定する
 * `init()` の途中で `start()` を呼ぶ。`start()` より前に届いたイベントは捨てずに保持する。
 *
 * `window` / `navigator` / `document` は直接触らず、注入された `view` 経由で使う
 * （`sound/flip-sound.ts` と同じ規律 — 単体テストがスタブ差し替えだけで回る）。
 */
import type { AnalyticsOptions } from '../core/options';
import type { DisposeRegistry } from '../core/dispose-registry';
import type { InternalBus } from '../core/events';
import { createAdapters } from './adapters';
import { SESSION_IDLE_MS, SessionId } from './session-id';
import type { AnalyticsAdapter, AnalyticsEvent, AnalyticsEventName, AnalyticsValue } from './types';

/** この件数が溜まったら送る。 */
const FLUSH_SIZE = 20;

/** 最初の 1 件からこの時間が経ったら送る。 */
const FLUSH_MS = 15000;

/** 送信路が死んでいるときの保持上限（超えたら古いものから捨てる）。 */
const MAX_BUFFER = 100;

/** 保存する検索語の長さ（サーバ側の上限と揃える。既定では保存されない）。 */
const MAX_TERM = 64;

export interface AnalyticsDeps {
  view: Window | null;
  registry: DisposeRegistry;
  bus: InternalBus;
  options: AnalyticsOptions;
  /** 現在の誌面の状態（page_view に載せる）。 */
  snapshot: () => { page: number; spread: boolean };
}

export class Analytics {
  private readonly view: Window | null;
  private readonly sessions: SessionId | null;
  private buffer: AnalyticsEvent[] = [];
  private adapters: AnalyticsAdapter[] = [];
  private book: string | null = null;
  private apiBase: string | null = null;
  private sid = '';
  private firstBatch = true;
  private cancelTimer: (() => void) | null = null;
  /** 現在滞在中のページと、その開始時刻（page_leave の材料）。 */
  private viewing: { page: number; since: number } | null = null;
  private stopped = false;

  constructor(private readonly deps: AnalyticsDeps) {
    this.view = deps.view;
    this.sessions = this.view === null ? null : new SessionId(this.view);
    if (this.view === null) return;

    this.subscribe();
    this.watchVisibility();
    // 最後の 1 件を落とさないための締め。コンストラクタで登録するので、
    // disposeAll() の逆順により「実行中のタイマを止める → ここで締める → 他を壊す」になる。
    deps.registry.add('other', () => this.end());
  }

  /** 宛先が確定したら呼ぶ（マニフェスト取得後）。 */
  start(book: string, apiBase: string | null): void {
    if (this.view === null || this.stopped) return;
    this.book = book;
    this.apiBase = apiBase;
    this.adapters = createAdapters(this.deps.options, this.view, apiBase);
    if (this.adapters.length === 0) {
      // 送り先が 1 つも無いなら購読は残っていても意味がない。溜めずに捨てる。
      this.buffer = [];
      this.stopped = true;
      return;
    }
    this.firstBatch = this.sessions?.isNew(book) ?? true;
    this.sid = this.sessions?.current(book) ?? '';
    // start 前に溜まった分にもセッション ID を入れてから送る
    for (const event of this.buffer) event.sid = this.sid;
    this.flush();
  }

  // --- バスの購読（ここだけが公開/内部イベントを分析語彙へ翻訳する） ---

  private subscribe(): void {
    const { bus } = this.deps;

    // 初回ページは afterPageChange が出ない（init が engine を直接動かす）ので ready を起点にする
    bus.on('ready', () => this.enterPage(this.deps.snapshot().page));
    bus.on('afterPageChange', ({ page }) => this.enterPage(page));
    bus.on('viewer:zoom', ({ level, page }) => {
      this.push('zoom', page, { zoom_level: Math.round(level * 100) / 100 });
    });
    bus.on('overlay:click', ({ id, type, page }) => {
      this.push('overlay_click', page, { overlay_id: id, overlay_type: type });
    });
    bus.on('searchDone', ({ query, hits }) => {
      // 空クエリの通知（起動時・クリア時）は操作ではないので数えない
      if (query === '') return;
      this.push('search', undefined, {
        hit_count: hits.length,
        search_term: query.slice(0, MAX_TERM),
      });
    });
  }

  /** ページに入った。直前のページがあれば滞在を締めてから積む。 */
  private enterPage(page: number): void {
    const now = this.now();
    if (this.viewing?.page === page) return;
    this.leavePage(now);
    this.viewing = { page, since: now };
    this.push('page_view', page, { spread: this.deps.snapshot().spread });
  }

  /**
   * 滞在を締める。バックグラウンドに置かれた時間は差し引かないが、
   * 放置による外れ値が日次平均を壊さないよう 30 分でクリップする。
   */
  private leavePage(now: number): void {
    if (this.viewing === null) return;
    const { page, since } = this.viewing;
    this.viewing = null;
    this.push('page_leave', page, {
      dwell_ms: Math.min(SESSION_IDLE_MS, Math.max(0, now - since)),
    });
  }

  // --- バッファと送出 ---

  private push(
    event: AnalyticsEventName,
    page: number | undefined,
    value: AnalyticsValue,
  ): void {
    if (this.stopped) return;
    this.buffer.push({ event, ts: this.now(), sid: this.sid, page, value });
    if (this.buffer.length > MAX_BUFFER)
      this.buffer.splice(0, this.buffer.length - MAX_BUFFER);
    if (this.buffer.length >= FLUSH_SIZE) {
      this.flush();
      return;
    }
    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.cancelTimer !== null || this.book === null) return;
    this.cancelTimer = this.deps.registry.timeout(() => {
      this.cancelTimer = null;
      this.flush();
    }, FLUSH_MS);
  }

  private flush(): void {
    this.cancelTimer?.();
    this.cancelTimer = null;
    if (
      this.book === null ||
      this.buffer.length === 0 ||
      this.adapters.length === 0
    )
      return;

    const events = this.buffer;
    this.buffer = [];
    const context = {
      book: this.book,
      apiBase: this.apiBase,
      firstBatch: this.firstBatch,
      referrer: this.referrer(),
    };
    this.firstBatch = false;
    for (const adapter of this.adapters) adapter.send(events, context);
  }

  /** 最後の締め（`pagehide` と `dispose()`）。 */
  private end(): void {
    if (this.stopped) return;
    this.leavePage(this.now());
    this.flush();
    this.stopped = true;
    for (const adapter of this.adapters) adapter.dispose?.();
  }

  // --- 離脱・復帰の検知 ---

  private watchVisibility(): void {
    const view = this.view;
    if (view === null) return;
    const { registry } = this.deps;

    // タブが隠れたら送るだけ（まだ同じページを見ているので page_leave は出さない）
    registry.listen(view, 'visibilitychange', () => {
      if (view.document?.visibilityState === 'hidden') this.flush();
    });
    // beforeunload は bfcache を壊すので使わない
    registry.listen(view, 'pagehide', () => this.end());
    // bfcache から戻ったら計測を再開する（end() 済みなので溜め直す）
    registry.listen(view, 'pageshow', (event) => {
      if ((event as PageTransitionEvent).persisted) this.resume();
    });
  }

  private resume(): void {
    if (this.book === null || this.adapters.length === 0) return;
    this.stopped = false;
    this.sid = this.sessions?.current(this.book) ?? this.sid;
    this.enterPage(this.deps.snapshot().page);
  }

  private referrer(): string | null {
    try {
      const ref = this.view?.document?.referrer;
      return typeof ref === 'string' && ref !== '' ? ref : null;
    } catch {
      return null;
    }
  }

  private now(): number {
    return Date.now();
  }
}
